import { KeyframeTrack } from 'three';

import { HAND_OPTIONS } from '@cartwhl/common/types';

export function quaternionsByOptionAndJoint(
  option: HAND_OPTIONS,
  track: KeyframeTrack,
) {
  const trackName = track.name.split('.')[0] as keyof HandKeys;
  const quats = QUATERNIONS_BY_OPTION[option];
  if (!quats) {
    console.warn(
      `${option} is not yet implemented or is an invalid hand option`,
    );
    return track.values;
  }
  const newQuaternion = quats[trackName];

  if (newQuaternion) {
    return track.values.map((value, index) => newQuaternion[index % 4]);
  } else if (trackName.includes('left')) {
    return track.values.map((value, index) => quats.left_default[index % 4]);
  } else if (trackName.includes('right')) {
    return track.values.map((value, index) => quats.right_default[index % 4]);
  } else {
    console.warn(`${option} is missing quaternions for ${trackName}`);
    return track.values;
  }
}

export const FINGER_BONES: Record<string, boolean> = {
  'left_thumb1.quaternion': true,
  'left_thumb2.quaternion': true,
  'left_thumb3.quaternion': true,
  'right_thumb1.quaternion': true,
  'right_thumb2.quaternion': true,
  'right_thumb3.quaternion': true,
  'left_index1.quaternion': true,
  'left_index2.quaternion': true,
  'left_index3.quaternion': true,
  'right_index1.quaternion': true,
  'right_index2.quaternion': true,
  'right_index3.quaternion': true,
  'left_middle1.quaternion': true,
  'left_middle2.quaternion': true,
  'left_middle3.quaternion': true,
  'right_middle1.quaternion': true,
  'right_middle2.quaternion': true,
  'right_middle3.quaternion': true,
  'left_ring1.quaternion': true,
  'left_ring2.quaternion': true,
  'left_ring3.quaternion': true,
  'right_ring1.quaternion': true,
  'right_ring2.quaternion': true,
  'right_ring3.quaternion': true,
  'left_pinky1.quaternion': true,
  'left_pinky2.quaternion': true,
  'left_pinky3.quaternion': true,
  'right_pinky1.quaternion': true,
  'right_pinky2.quaternion': true,
  'right_pinky3.quaternion': true,
};

type QuatArray = [number, number, number, number];
type HandKeys = {
  left_default: QuatArray;
  right_default: QuatArray;
  left_thumb1: QuatArray;
  left_thumb2: QuatArray;
  left_thumb3: QuatArray;
  right_thumb1: QuatArray;
  right_thumb2: QuatArray;
  right_thumb3: QuatArray;
  left_index1: QuatArray;
  left_index2: QuatArray;
  left_index3: QuatArray;
  right_index1: QuatArray;
  right_index2: QuatArray;
  right_index3: QuatArray;
  left_middle1: QuatArray;
  left_middle2: QuatArray;
  left_middle3: QuatArray;
  right_middle1: QuatArray;
  right_middle2: QuatArray;
  right_middle3: QuatArray;
  left_ring1: QuatArray;
  left_ring2: QuatArray;
  left_ring3: QuatArray;
  right_ring1: QuatArray;
  right_ring2: QuatArray;
  right_ring3: QuatArray;
  left_pinky1: QuatArray;
  left_pinky2: QuatArray;
  left_pinky3: QuatArray;
  right_pinky1: QuatArray;
  right_pinky2: QuatArray;
  right_pinky3: QuatArray;
};

// TODO: QUATERNIONS_BY_OPTION can be typed to require all hand options
export const QUATERNIONS_BY_OPTION: Record<
  string,
  Partial<Omit<HandKeys, 'left_default' | 'right_default'>> &
    Required<Pick<HandKeys, 'left_default' | 'right_default'>>
> = {
  [HAND_OPTIONS.CLAW]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.19914502485385005, -0.2690085932164903, -0.2154401877051934,
      0.9173664269926143,
    ],
    left_thumb2: [
      0.0634786308366953, 0.3353270236668761, 0.05846468455589603,
      0.9381407843631959,
    ],
    left_thumb3: [
      0.11064345172561889, 0.47961290759494396, 0.020549784033835727,
      0.8702339868303967,
    ],
    left_index1: [
      0.039193855191852145, -0.24038432717195096, 0.17465959012330626,
      0.9540300019093987,
    ],
    left_index2: [0, 0, -0.390189544208527, 0.9207345543588239],
    left_index3: [0, 0, -0.3566213467328018, 0.9342490112675971],
    left_middle1: [
      -0.009410880540770099, -0.08110788707423884, 0.21905177925913724,
      0.9722907301754995,
    ],
    left_middle2: [0, 0, -0.3901895442085269, 0.9207345543588239],
    left_middle3: [0, 0, -0.3566213467328018, 0.9342490112675971],
    left_ring1: [
      0.015202266632881274, 0.13100341174084176, 0.21786591544945924,
      0.9670271144527702,
    ],
    left_ring2: [0, 0, -0.3901895442085269, 0.9207345543588239],
    left_ring3: [0, 0, -0.3566213467328019, 0.9342490112675971],
    left_pinky1: [
      0.026904205208938603, 0.23231819556450498, 0.21369049708754365,
      0.9484939594990609,
    ],
    left_pinky2: [0, 0, -0.3901895442085269, 0.9207345543588239],
    left_pinky3: [0, 0, -0.356621346732802, 0.9342490112675971],
    right_thumb1: [
      0.19913616732096448, 0.269008609228817, 0.21544835495380288,
      0.9173664269926138,
    ],
    right_thumb2: [
      0.06347967709314675, -0.33532692748542464, -0.05846410022885847,
      0.9381407843622217,
    ],
    right_thumb3: [
      0.1106450847520577, -0.4796125648305621, -0.020548991268014724,
      0.8702339868303965,
    ],
    right_index1: [
      0.039186643007879454, 0.2403855029831966, -0.17465959012329532,
      0.9540300019094006,
    ],
    right_index2: [0, 0, 0.39018954420851637, 0.9207345543588283],
    right_index3: [0, 0, 0.3566213467328019, 0.9342490112675971],
    right_middle1: [
      -0.009412132858428398, 0.0811077417590664, -0.21905177925913735,
      0.9722907301754995,
    ],
    right_middle2: [0, 0, 0.3901895442085269, 0.9207345543588239],
    right_middle3: [0, 0, 0.3566213467328019, 0.9342490112675971],
    right_ring1: [
      0.015202266632881295, -0.1310034117408418, -0.21786591544945919,
      0.9670271144527702,
    ],
    right_ring2: [0, 0, 0.3901895442085269, 0.9207345543588239],
    right_ring3: [0, 0, 0.3566213467328019, 0.9342490112675971],
    right_pinky1: [
      0.02687847287273823, -0.23232117411958336, -0.21369049708737775,
      0.9484939594990984,
    ],
    right_pinky2: [0, 0, 0.3901895442083657, 0.9207345543588922],
    right_pinky3: [0, 0, 0.3566213467328019, 0.9342490112675971],
  },
  [HAND_OPTIONS.CURLED]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.08576317589369148, -0.0111765808534749, -0.16748270986781524,
      0.9820739807144809,
    ],
    left_thumb2: [
      0.06879494097583563, 0.371580513071773, 0.042902611107050764,
      0.9248537962087016,
    ],
    left_thumb3: [
      -0.0043901816886527684, -0.023191214738074666, -0.004043417197048054,
      0.9997132312023702,
    ],
    left_index1: [
      0.05273924346946252, -0.03618262479043709, -0.2979521198037256,
      0.9524357847991695,
    ],
    left_index2: [0, 0, -0.6178838618434105, 0.7862693770416557],
    left_index3: [0, 0, -0.194745024449836, 0.9808539011759257],
    left_middle1: [
      0.02123537170772916, -0.0020396516165959505, -0.4510420826134056,
      0.8922476889975559,
    ],
    left_middle2: [0, 0, -0.5800414924820992, 0.81458693028991],
    left_middle3: [0, 0, -0.42084217734743024, 0.9071338720197114],
    left_ring1: [
      -0.03467695074365648, 0.011627849197849018, -0.4916886067004064,
      0.8700026530138671,
    ],
    left_ring2: [0, 0, -0.6512819950796999, 0.7588357944147112],
    left_ring3: [0, 0, -0.4594646767454768, 0.8881960430125629],
    left_pinky1: [
      -0.13651060283183325, 0.05122908703872592, -0.514270913370689,
      0.8451425108326673,
    ],
    left_pinky2: [0, 0, -0.6413292505529383, 0.7672657899223752],
    left_pinky3: [0, 0, -0.45449765312367474, 0.8907479347745196],
    right_thumb1: [
      0.08576071630329846, 0.01117504939853478, 0.16748407152233594,
      0.9820739807144809,
    ],
    right_thumb2: [
      0.06879614773512313, -0.3715803647977599, -0.04290196025704214,
      0.9248537962076352,
    ],
    right_thumb3: [
      -0.00439025404770868, 0.02319120808598967, 0.004043376784962929,
      0.9997132312023701,
    ],
    right_index1: [
      0.05273815787117348, 0.03618420709110505, 0.2979521198037366,
      0.9524357847991658,
    ],
    right_index2: [0, 0, 0.6178838618434013, 0.7862693770416628],
    right_index3: [0, 0, 0.19474502444983588, 0.9808539011759259],
    right_middle1: [
      0.021235340212649948, 0.0020399794938964428, 0.4510420826134056,
      0.8922476889975559,
    ],
    right_middle2: [0, 0, 0.5800414924820992, 0.81458693028991],
    right_middle3: [0, 0, 0.42084217734743024, 0.9071338720197114],
    right_ring1: [
      -0.03467695074365649, -0.011627849197849123, 0.4916886067004064,
      0.8700026530138671,
    ],
    right_ring2: [0, 0, 0.6512819950796999, 0.7588357944147112],
    right_ring3: [0, 0, 0.4594646767454768, 0.8881960430125629],
    right_pinky1: [
      -0.13651627626216634, -0.05121396645294931, 0.5142709133708373,
      0.8451425108325773,
    ],
    right_pinky2: [0, 0, 0.6413292505528038, 0.7672657899224877],
    right_pinky3: [0, 0, 0.45449765312367496, 0.8907479347745195],
  },
  [HAND_OPTIONS.FIST_CLENCHED]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.005646371351089981, 0.2240389254897445, -0.20455254749456087,
      0.9528551483145022,
    ],
    left_thumb2: [
      0.12934185094011824, 0.6832506835708692, 0.11912560834123165,
      0.7086947709928835,
    ],
    left_thumb3: [
      0.12644889629689, 0.6679685979724528, 0.11646115766828086,
      0.7240617566910836,
    ],
    left_index1: [
      0.042269595050327645, 0.01161395004976521, -0.6804046620417172,
      0.7315243628003236,
    ],
    left_index2: [0, 0, -0.8271717675069105, 0.5619491676650955],
    left_index3: [0, 0, -0.7510284139850367, 0.6602698852644426],
    left_middle1: [
      0.007889707826217035, 0.020422739144070418, -0.6553323391810586,
      0.755023303918331,
    ],
    left_middle2: [0, 0, -0.8249198653006274, 0.5652496933501111],
    left_middle3: [0, 0, -0.7621953876079133, 0.6473470407047697],
    left_ring1: [
      -0.0354583430390855, 0.029750396883375704, -0.6801200163474863,
      0.7316381504252627,
    ],
    left_ring2: [0, 0, -0.8054784595367819, 0.592625051126134],
    left_ring3: [0, 0, -0.7621953876079135, 0.6473470407047696],
    left_pinky1: [
      -0.09512228413905602, 0.014584633835203697, -0.7164288084307275,
      0.6909911735808132,
    ],
    left_pinky2: [0, 0, -0.7638819865681274, 0.6453559565051918],
    left_pinky3: [0, 0, -0.8118322859578864, 0.5838906913767272],
    right_thumb1: [
      0.005648682190111276, -0.22404301165579885, 0.20454800817476257,
      0.9528551483145022,
    ],
    right_thumb2: [
      0.1293439827552647, -0.6832504875916107, -0.11912441773620586,
      0.7086947709908985,
    ],
    right_thumb3: [
      0.12645098042993963, -0.6679684063747215, -0.11645999369287108,
      0.7240617566910837,
    ],
    right_index1: [
      0.04226994348055719, -0.011612681844760282, 0.6804046620417253,
      0.731524362800316,
    ],
    right_index2: [0, 0, -0.8271717675069044, -0.5619491676651045],
    right_index3: [0, 0, -0.751028413985037, -0.6602698852644425],
    right_middle1: [
      0.007890023155643243, -0.02042261732328237, 0.6553323391810585,
      0.7550233039183308,
    ],
    right_middle2: [0, 0, -0.8249198653006276, -0.5652496933501111],
    right_middle3: [0, 0, -0.7621953876079135, -0.6473470407047696],
    right_ring1: [
      -0.0354583430390855, -0.029750396883375784, 0.6801200163474863,
      0.7316381504252627,
    ],
    right_ring2: [0, 0, -0.8054784595367821, -0.5926250511261336],
    right_ring3: [0, 0, -0.7621953876079135, -0.6473470407047696],
    right_pinky1: [
      0.09512389898776102, 0.014574097752675139, -0.7164288084308482,
      -0.6909911735806881,
    ],
    right_pinky2: [0, 0, -0.7638819865680149, -0.6453559565053252],
    right_pinky3: [0, 0, -0.8118322859578867, -0.5838906913767269],
  },
  [HAND_OPTIONS.FIST_RELAXED]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      -0.07694989589281136, 0.046684098065951925, 0.04011334386971774,
      0.9951332715538376,
    ],
    left_thumb2: [
      0.07958225688608345, 0.420394721602544, 0.07329634372645517,
      0.9008676864190269,
    ],
    left_thumb3: [
      -0.035389694508055715, -0.1869467058669831, -0.032594391193878205,
      0.9811911660582817,
    ],
    left_index1: [
      0.0529798136254379, -0.005335949255680475, -0.17923507768542513,
      0.982364216531147,
    ],
    left_index2: [0, 0, -0.8703162574306567, 0.492493260920284],
    left_index3: [0, 0, -0.4621073382110033, 0.886824000561296],
    left_middle1: [
      0.009300923258815666, -0.025005654647510436, -0.22993430010101032,
      0.9728403916877844,
    ],
    left_middle2: [0, 0, -0.8575093742834409, 0.5144683401493446],
    left_middle3: [0, 0, -0.5723126039562771, 0.8200355378596622],
    left_ring1: [
      -0.0592825356187901, 0.0007799796997713764, -0.38024101757048673,
      0.9229852334459289,
    ],
    left_ring2: [0, 0, -0.8054784595367819, 0.592625051126134],
    left_ring3: [0, 0, -0.6094544646624181, 0.7928210740785372],
    left_pinky1: [
      -0.18855662025360997, 0.06838287220122685, -0.3982454138706369,
      0.8950814343282133,
    ],
    left_pinky2: [0, 0, -0.8605034080822274, 0.50944468264854],
    left_pinky3: [0, 0, -0.512380665977851, 0.8587584370078083],
    right_thumb1: [
      -0.07694832277149331, -0.046684992554155105, -0.04011532050761248,
      0.9951332715538377,
    ],
    right_thumb2: [
      0.07958356856277836, -0.42039460102047616, -0.07329561116382559,
      0.9008676864178053,
    ],
    right_thumb3: [
      -0.035390277801655635, 0.18694665224385648, 0.03259406542801237,
      0.9811911660582818,
    ],
    right_index1: [
      0.052979653508986015, 0.005337538787973558, 0.17923507768543648,
      0.9823642165311448,
    ],
    right_index2: [0, 0, -0.8703162574306511, -0.4924932609202938],
    right_index3: [0, 0, 0.4621073382110033, 0.886824000561296],
    right_middle1: [
      0.009300537166390212, 0.025005798252275956, 0.2299343001010103,
      0.9728403916877844,
    ],
    right_middle2: [0, 0, -0.8575093742834412, -0.5144683401493442],
    right_middle3: [0, 0, 0.5723126039562769, 0.8200355378596624],
    right_ring1: [
      -0.05928253561879011, -0.0007799796997715013, 0.38024101757048673,
      0.9229852334459288,
    ],
    right_ring2: [0, 0, -0.8054784595367821, -0.5926250511261336],
    right_ring3: [0, 0, 0.6094544646624181, 0.7928210740785372],
    right_pinky1: [
      -0.18856419336280808, -0.06836198675716582, 0.39824541387079343,
      0.8950814343281437,
    ],
    right_pinky2: [0, 0, -0.8605034080821385, -0.50944468264869],
    right_pinky3: [0, 0, 0.5123806659778508, 0.8587584370078084],
  },
  [HAND_OPTIONS.FIST]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      -0.02949624309616262, -0.03583273056472683, 0.020370147872014487,
      0.9987146960664793,
    ],
    left_thumb2: [
      0.08687561334814378, 0.45892200996287424, 0.08001362447570504,
      0.8805912993411189,
    ],
    left_thumb3: [
      0.011759536504837749, 0.062119965788388624, 0.010830693466713101,
      0.9979406391317427,
    ],
    left_index1: [
      0.04874919030831493, 0.021420771473712144, -0.6393668310585999,
      0.767055879539319,
    ],
    left_index2: [0, 0, -0.7677710010597798, 0.6407243478530089],
    left_index3: [0, 0, -0.7621953876079133, 0.6473470407047697],
    left_middle1: [
      0.009823612765664442, 0.023863111027786512, -0.6311235916082603,
      0.7752529011102092,
    ],
    left_middle2: [0, 0, -0.805478459536782, 0.5926250511261337],
    left_middle3: [0, 0, -0.7621953876079133, 0.6473470407047697],
    left_ring1: [
      -0.01291811677309432, 0.02813868467175265, -0.6304253495640323,
      0.7756321391684643,
    ],
    left_ring2: [0, 0, -0.8054784595367819, 0.592625051126134],
    left_ring3: [0, 0, -0.7621953876079135, 0.6473470407047696],
    left_pinky1: [
      -0.06863704811778198, 0.048248013252402855, -0.6264145240688347,
      0.7749618886619448,
    ],
    left_pinky2: [0, 0, -0.7638819865681274, 0.6453559565051918],
    left_pinky3: [0, 0, -0.8118322859578864, 0.5838906913767272],
    right_thumb1: [
      -0.029496775565246238, 0.03583249051254731, -0.020369799109504906,
      0.9987146960664793,
    ],
    right_thumb2: [
      0.08687704523406446, -0.45892187832972114, -0.08001282477695154,
      0.8805912993397855,
    ],
    right_thumb3: [
      0.011759730325736217, -0.06211994797011892, -0.010830585218941036,
      0.9979406391317426,
    ],
    right_index1: [
      0.04874983296623854, -0.021419308859278226, 0.639366831058609,
      0.7670558795393114,
    ],
    right_index2: [0, 0, -0.7677710010597723, -0.640724347853018],
    right_index3: [0, 0, -0.7621953876079135, -0.6473470407047696],
    right_middle1: [
      0.009823981214753596, -0.023862959346786065, 0.6311235916082601,
      0.7752529011102092,
    ],
    right_middle2: [0, 0, -0.8054784595367821, -0.5926250511261336],
    right_middle3: [0, 0, -0.7621953876079135, -0.6473470407047696],
    right_ring1: [
      -0.012918116773094323, -0.028138684671752748, 0.6304253495640323,
      0.7756321391684643,
    ],
    right_ring2: [0, 0, -0.8054784595367821, -0.5926250511261336],
    right_ring3: [0, 0, -0.7621953876079135, -0.6473470407047696],
    right_pinky1: [
      -0.06864239177293142, -0.04824041053741336, 0.6264145240689702,
      0.7749618886618352,
    ],
    right_pinky2: [0, 0, -0.7638819865680149, -0.6453559565053252],
    right_pinky3: [0, 0, -0.8118322859578867, -0.5838906913767269],
  },

  [HAND_OPTIONS.GRIPPING]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.23155184329406303, 0.011335558925282332, -0.2731958988653778,
      0.9336055108096306,
    ],
    left_thumb2: [
      0.08611976264017066, 0.4549292148300807, 0.07931747566734945,
      0.8827975611829966,
    ],
    left_thumb3: [
      0.08082978444019068, 0.4269848086310073, 0.07444533361430125,
      0.8975569126516435,
    ],
    left_index1: [
      0.03877904425673308, -0.019646592232752044, -0.45048656303584433,
      0.8917242026906786,
    ],
    left_index2: [0, 0, -0.5995801375041189, 0.8003147247867816],
    left_index3: [0, 0, -0.18801522271107268, 0.9821661142744162],
    left_middle1: [0, 0, -0.4104774623151343, 0.9118707435439123],
    left_middle2: [0, 0, -0.5995801375041185, 0.8003147247867819],
    left_middle3: [0, 0, -0.18801522271107282, 0.9821661142744162],
    left_ring1: [
      -0.0014468119627950235, -0.0030852143041600827, -0.42458003515271614,
      0.9053839969524312,
    ],
    left_ring2: [0, 0, -0.5995801375041188, 0.8003147247867817],
    left_ring3: [0, 0, -0.18801522271107282, 0.9821661142744162],
    left_pinky1: [
      -0.04991702866033207, 0.016887863860308855, -0.4220120583453612,
      0.9050574086294586,
    ],
    left_pinky2: [0, 0, -0.5995801375041188, 0.8003147247867817],
    left_pinky3: [0, 0, -0.13433459666768105, 0.9909360302956651],
    right_thumb1: [
      0.23154849232516111, -0.011336007579698881, 0.2731987203849738,
      0.9336055108096304,
    ],
    right_thumb2: [
      0.08612118206814688, -0.4549290843422128, -0.07931668292628243,
      0.8827975611816746,
    ],
    right_thumb3: [
      0.08083111667819448, -0.426984686156207, -0.07444458956762204,
      0.8975569126516433,
    ],
    right_index1: [
      0.03877845478948995, 0.019647755697920714, 0.4504865630358545,
      0.8917242026906735,
    ],
    right_index2: [0, 0, 0.5995801375041093, 0.8003147247867887],
    right_index3: [0, 0, 0.18801522271107282, 0.9821661142744162],
    right_middle1: [0, 0, 0.4104774623151343, 0.9118707435439123],
    right_middle2: [0, 0, 0.5995801375041188, 0.8003147247867817],
    right_middle3: [0, 0, 0.18801522271107282, 0.9821661142744162],
    right_ring1: [
      -0.001446811962794981, 0.0030852143041599925, 0.42458003515271614,
      0.9053839969524312,
    ],
    right_ring2: [0, 0, 0.5995801375041188, 0.8003147247867817],
    right_ring3: [0, 0, 0.18801522271107282, 0.9821661142744162],
    right_pinky1: [
      -0.04991889889811783, -0.016882334816021256, 0.4220120583455198,
      0.9050574086293847,
    ],
    right_pinky2: [0, 0, 0.5995801375039784, 0.8003147247868868],
    right_pinky3: [0, 0, 0.13433459666768108, 0.9909360302956651],
  },
  [HAND_OPTIONS.LOVE_YOU]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.0024246048911448015, -0.1615828858928436, -0.01887102510787683,
      0.9866757201274592,
    ],
    left_thumb2: [
      -0.01629133016644087, -0.08605924835306078, -0.01500453722178261,
      0.9960438053605245,
    ],
    left_thumb3: [
      -0.039750140027147315, -0.20998083874770268, -0.03661042097329627,
      0.9762108639032818,
    ],
    left_index1: [
      -0.0037855580169633392, -0.07494845676929517, -0.04188883397544665,
      0.9963000170463608,
    ],
    left_index2: [
      0.002034282209628875, 0.003182595466069185, 0.08734930916621494,
      0.9961705832688377,
    ],
    left_index3: [0, 0, 0.1045386234697098, 0.9945208274355335],
    left_middle1: [
      0.05802227945090762, -0.011853682793531543, -0.5982550015184647,
      0.7991144213751199,
    ],
    left_middle2: [0, 0, -0.8328287231796373, 0.5535307740740122],
    left_middle3: [0, 0, -0.8548535575983016, 0.5188693429578656],
    left_ring1: [
      -0.02467122727662333, 0.055592757234175644, -0.6525213241379146,
      0.755325557248707,
    ],
    left_ring2: [0, 0, -0.7760074160123683, 0.6307237828826554],
    left_ring3: [0, 0, -0.7096184341866854, 0.7045861749015778],
    left_pinky1: [
      -1.565762333544342e-5, 0.06688414115725236, -2.710505431213761e-20,
      0.9977607485847975,
    ],
    left_pinky2: [0, 0, 0.09030036587513277, 0.9959145766193088],
    left_pinky3: [0, 0, 0.13329644883405622, 0.9910762113622896],
    right_thumb1: [
      0.0024207428864179547, 0.1615825514054274, 0.018874384656204186,
      0.986675720127459,
    ],
    right_thumb2: [
      -0.016291598679933544, 0.08605922366541847, 0.015004387257899147,
      0.9960438053607746,
    ],
    right_thumb3: [
      -0.03975079518969094, 0.20998077851754762, 0.03661005506907041,
      0.9762108639032817,
    ],
    right_index1: [
      -0.003787806667419739, 0.07494834315879802, 0.041888833975458076,
      0.9963000170463603,
    ],
    right_index2: [
      0.0020342683565884004, -0.0031826043207474892, -0.08734930916622634,
      0.9961705832688365,
    ],
    right_index3: [0, 0, -0.1045386234697098, 0.9945208274355335],
    right_middle1: [
      0.05802209642122862, 0.011854578665415678, 0.5982550015184648,
      0.7991144213751201,
    ],
    right_middle2: [0, 0, -0.8328287231796371, -0.5535307740740124],
    right_middle3: [0, 0, -0.854853557598302, -0.5188693429578651],
    right_ring1: [
      -0.024671227276623325, -0.05559275723417573, 0.6525213241379146,
      0.755325557248707,
    ],
    right_ring2: [0, 0, -0.7760074160123683, -0.6307237828826554],
    right_ring3: [0, 0, -0.7096184341866854, -0.7045861749015779],
    right_pinky1: [
      -2.3065885717607415e-5, -0.06688413901269386, -8.185514644028745e-18,
      0.9977607485847976,
    ],
    right_pinky2: [0, 0, -0.09030036587513439, 0.9959145766193087],
    right_pinky3: [0, 0, -0.13329644883405622, 0.9910762113622896],
  },
  [HAND_OPTIONS.OPEN_LOOSE]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.059174005145633285, -0.08653918011164735, -0.06555861601434686,
      0.9923262947674578,
    ],
    left_thumb2: [
      0.021589713717707452, 0.11404805597340534, 0.019884420717974447,
      0.9930415575407631,
    ],
    left_thumb3: [
      -0.03595097689258718, -0.1899116902871068, -0.03311133992333064,
      0.9805838650116798,
    ],
    left_index1: [
      0.06105725073301666, -0.09294983515936277, -0.09866806854436583,
      0.9888867238094045,
    ],
    left_index2: [0, 0, -0.12919074521512736, 0.9916197614765249],
    left_index3: [0, 0, -0.09543540145069533, 0.9954356253168483],
    left_middle1: [
      0.006371388400668129, 0.01737219867728763, -0.07256458958006802,
      0.997192053950512,
    ],
    left_middle2: [0, 0, -0.18952684476533424, 0.9818755395228547],
    left_middle3: [0, 0, -0.0954354014506954, 0.9954356253168483],
    left_ring1: [
      0.005526851889865998, 0.03046501639925886, -0.07254221858615677,
      0.9968846288345401,
    ],
    left_ring2: [0, 0, -0.18952684476533424, 0.9818755395228547],
    left_ring3: [0, 0, -0.09543540145069536, 0.9954356253168483],
    left_pinky1: [
      -0.12126640748881573, 0.10664059721639316, -0.1534483951815005,
      0.9748722128855196,
    ],
    left_pinky2: [0, 0, 0, 1],
    left_pinky3: [0, 0, -0.17525669929054177, 0.9845227723896409],
    right_thumb1: [
      0.059171205153729096, 0.08653915309717133, 0.06556117887109686,
      0.9923262947674578,
    ],
    right_thumb2: [
      0.021590069560279822, -0.11404802326297396, -0.019884221982857084,
      0.9930415575404321,
    ],
    right_thumb3: [
      -0.03595156943725363, 0.18991163581351483, 0.033111008990801286,
      0.9805838650116799,
    ],
    right_index1: [
      0.06105446196435799, 0.09295166699672429, 0.09866806854437721,
      0.9888867238094031,
    ],
    right_index2: [0, 0, 0.129190745215116, 0.9916197614765263],
    right_index3: [0, 0, 0.09543540145069536, 0.9954356253168483],
    right_middle1: [
      0.006371656629441445, -0.017372100299958598, 0.07256458958006799,
      0.997192053950512,
    ],
    right_middle2: [0, 0, 0.18952684476533424, 0.9818755395228547],
    right_middle3: [0, 0, 0.09543540145069536, 0.9954356253168483],
    right_ring1: [
      0.005526851889865998, -0.03046501639925899, 0.07254221858615677,
      0.9968846288345401,
    ],
    right_ring2: [0, 0, 0.18952684476533424, 0.9818755395228547],
    right_ring3: [0, 0, 0.09543540145069536, 0.9954356253168483],
    right_pinky1: [
      -0.12127821853650214, -0.10662716477767265, 0.15344839518167103,
      0.9748722128854926,
    ],
    right_pinky2: [0, 0, 0, 1],
    right_pinky3: [0, 0, 0.17525669929054177, 0.9845227723896409],
  },
  [HAND_OPTIONS.OPEN_TIGHT]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      -0.2763673110758765, 0.08359667321435915, -0.01824017886277441,
      0.9572356039508952,
    ],
    left_thumb2: [
      0.055440314204053834, 0.29286446963589785, 0.05106128533172545,
      0.953178639671553,
    ],
    left_thumb3: [
      0.016354987503136555, 0.08639551939367732, 0.01506316649687036,
      0.9960127657954775,
    ],
    left_index1: [
      0.0869444537784727, 0.05102405393180332, -0.1403902077109986,
      0.9849506573714188,
    ],
    left_index2: [0, 0, -0.08599036750639351, 0.9962959684230963],
    left_index3: [0, 0, -0.10708313105354479, 0.9942500706782823],
    left_middle1: [
      0.009364727709919174, 0.04871481854770201, -0.12546600630462773,
      0.9908569269025426,
    ],
    left_middle2: [0, 0, -0.12394336792365618, 0.9922892932747693],
    left_middle3: [0, 0, -0.14529975644823018, 0.9893876797171496],
    left_ring1: [
      0.005017090163907769, 0.02339727347841155, -0.16452903571032204,
      0.9860819402099884,
    ],
    left_ring2: [0, 0, -0.08106585342493258, 0.9967087475328423],
    left_ring3: [0, 0, -0.060371765847234284, 0.9981759613858103],
    left_pinky1: [
      -0.051159595070558575, 0.010813172071989625, -0.11921579872978275,
      0.9914904762402803,
    ],
    left_pinky2: [0, 0, -0.1231912634900453, 0.9923829465482195],
    left_pinky3: [0, 0, -0.06359594993257153, 0.9979757287390179],
    right_thumb1: [
      -0.2763656823889732, -0.08360322837641786, 0.01823481098013746,
      0.9572356039508958,
    ],
    right_thumb2: [
      0.055441227972829354, -0.2928643856342951, -0.05106077499824606,
      0.9531786396707019,
    ],
    right_thumb3: [
      0.016355257066343373, -0.08639549461229475, -0.015063015947487834,
      0.9960127657954774,
    ],
    right_index1: [
      0.08694598459606608, -0.051021445345003726, 0.14039020771100982,
      0.9849506573714173,
    ],
    right_index2: [0, 0, 0.08599036750638218, 0.9962959684230973],
    right_index3: [0, 0, 0.1070831310535448, 0.9942500706782823],
    right_middle1: [
      0.009365479873411495, -0.04871467394899811, 0.12546600630462776,
      0.9908569269025426,
    ],
    right_middle2: [0, 0, 0.12394336792365622, 0.9922892932747693],
    right_middle3: [0, 0, 0.14529975644823015, 0.9893876797171496],
    right_ring1: [
      0.0050170901639077685, -0.02339727347841166, 0.16452903571032204,
      0.9860819402099884,
    ],
    right_ring2: [0, 0, 0.08106585342493258, 0.9967087475328423],
    right_ring3: [0, 0, 0.060371765847234284, 0.9981759613858103],
    right_pinky1: [
      -0.051160792451966225, -0.010807505435058647, 0.11921579872995619,
      0.9914904762402595,
    ],
    right_pinky2: [0, 0, 0.12319126348987176, 0.992382946548241],
    right_pinky3: [0, 0, 0.06359594993257145, 0.997975728739018],
  },
  [HAND_OPTIONS.PEACE]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      -0.07028020911245507, 0.008480655768363845, 0.011382323409194088,
      0.997426294719894,
    ],
    left_thumb2: [
      0.12633895903709502, 0.6673878523950595, 0.11635990395297352,
      0.7246325238746797,
    ],
    left_thumb3: [
      0.1216187338721888, 0.6424531769818889, 0.11201251221402675,
      0.7482753477595703,
    ],
    left_index1: [
      -9.561772170812607e-5, -0.08321568857499527, -1.6940658945086007e-21,
      0.9965315549606248,
    ],
    left_index2: [
      0.0008725387520444692, 0.005023998454942169, 0.04998771161814421,
      0.9987368155839816,
    ],
    left_index3: [0.0, 0.0, 0.14201692109689057, 0.9898642301458113],
    left_middle1: [
      -5.278864530197024e-7, 0.03418918209633086, 6.617444900424222e-22,
      0.9994153790228093,
    ],
    left_middle2: [
      0.0008725387520444679, 0.005023998454942154, 0.049987711618143964,
      0.9987368155839816,
    ],
    left_middle3: [0.0, 0.0, 0.14201692109689057, 0.9898642301458113],
    left_ring1: [
      -0.03918052532404906, 0.03885894919426838, -0.6518120259025214,
      0.7563702475584954,
    ],
    left_ring2: [0.0, 0.0, -0.7731183456231768, 0.6342617942623394],
    left_ring3: [0.0, 0.0, -0.5409753412595094, 0.8410384534307319],
    left_pinky1: [
      -0.10064392811975992, -0.007182700989368802, -0.6239586316109653,
      0.7749160177575979,
    ],
    left_pinky2: [0.0, 0.0, -0.8204651001794813, 0.5716966148119769],
    left_pinky3: [0.0, 0.0, -0.700459996825794, 0.7136916651095266],
    right_thumb1: [
      -0.07027986928877608, -0.008481992822223998, -0.01138342531312295,
      0.9974262947198941,
    ],
    right_thumb2: [
      0.12634104135853003, -0.6673876609658824, -0.11635874098989157,
      0.7246325238727406,
    ],
    right_thumb3: [
      0.12162073839441156, -0.6424529927029103, -0.11201139270077132,
      0.7482753477595704,
    ],
    right_index1: [
      -9.81144126694331e-5, 0.0832156856687566, 1.6940658945086007e-21,
      0.9965315549606248,
    ],
    right_index2: [
      0.0008725168838604697, -0.00502400225283193, -0.0499877116181444,
      0.9987368155839815,
    ],
    right_index3: [0.0, 0.0, -0.14201692109689057, 0.9898642301458113],
    right_middle1: [0.0, -0.0341891821004063, 0.0, 0.9994153790228092],
    right_middle2: [
      0.0008725121866795145, -0.005024003068587585, -0.04998771161814397,
      0.9987368155839818,
    ],
    right_middle3: [0.0, 0.0, -0.14201692109689057, 0.9898642301458113],
    right_ring1: [
      -0.03918052532404905, -0.03885894919426849, 0.6518120259025214,
      0.7563702475584954,
    ],
    right_ring2: [0.0, 0.0, -0.773118345623177, -0.6342617942623391],
    right_ring3: [0.0, 0.0, 0.5409753412595094, 0.8410384534307319],
    right_pinky1: [
      -0.10064313192763819, 0.007193848530106496, 0.6239586316111005,
      0.7749160177574891,
    ],
    right_pinky2: [0.0, 0.0, -0.8204651001793817, -0.5716966148121198],
    right_pinky3: [0.0, 0.0, 0.700459996825794, 0.7136916651095266],
  },
  [HAND_OPTIONS.POINTING_COOL]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.024480060217023956, -0.10045517380312166, -0.03201858960509915,
      0.9941248888483055,
    ],
    left_thumb2: [
      -0.01629133016644085, -0.08605924835306064, -0.015004537221782582,
      0.9960438053605245,
    ],
    left_thumb3: [
      -0.039750140027147274, -0.20998083874770268, -0.03661042097329625,
      0.9762108639032818,
    ],
    left_index1: [
      0.04750334676440867, 0.052160745645167275, -0.026190170709024033,
      0.9971643613859014,
    ],
    left_index2: [0, 0, 0.07554714287294707, 0.9971422311805546],
    left_index3: [0, 0, 0.04065553747777228, 0.9991732218551463],
    left_middle1: [
      0.010819898825870226, 0.0234282142958473, -0.5979136262706214,
      0.8011450206324828,
    ],
    left_middle2: [0, 0, -0.8328287231796372, 0.5535307740740123],
    left_middle3: [0, 0, -0.5723126039562771, 0.8200355378596622],
    left_ring1: [
      -0.05358922107984755, 0.022035938922829526, -0.650784867136665,
      0.7570479968177433,
    ],
    left_ring2: [0, 0, -0.8054784595367819, 0.592625051126134],
    left_ring3: [0, 0, -0.6094544646624181, 0.7928210740785372],
    left_pinky1: [
      -0.10064392811975992, -0.007182700989368802, -0.6239586316109653,
      0.7749160177575979,
    ],
    left_pinky2: [0, 0, -0.8204651001794813, 0.5716966148119769],
    left_pinky3: [0, 0, -0.700459996825794, 0.7136916651095266],
    right_thumb1: [
      0.024477391575587162, 0.10045506174448561, 0.03202098130775054,
      0.9941248888483054,
    ],
    right_thumb2: [
      -0.016291598679933524, 0.08605922366541847, 0.015004387257899165,
      0.9960438053607746,
    ],
    right_thumb3: [
      -0.039750795189690984, 0.20998077851754773, 0.03661005506907042,
      0.9762108639032817,
    ],
    right_index1: [
      0.047504911703514965, -0.05215932039550317, 0.026190170709035458,
      0.997164361385901,
    ],
    right_index2: [0, 0, -0.07554714287295852, 0.9971422311805537],
    right_index3: [0, 0, -0.0406555374777723, 0.9991732218551463],
    right_middle1: [
      0.010820260559965367, -0.023428047232082205, 0.5979136262706217,
      0.8011450206324827,
    ],
    right_middle2: [0, 0, -0.8328287231796371, -0.5535307740740124],
    right_middle3: [0, 0, 0.5723126039562769, 0.8200355378596624],
    right_ring1: [
      -0.053589221079847545, -0.022035938922829616, 0.650784867136665,
      0.7570479968177433,
    ],
    right_ring2: [0, 0, -0.8054784595367821, -0.5926250511261336],
    right_ring3: [0, 0, 0.6094544646624181, 0.7928210740785372],
    right_pinky1: [
      -0.10064313192763819, 0.007193848530106496, 0.6239586316111005,
      0.7749160177574891,
    ],
    right_pinky2: [0, 0, -0.8204651001793817, -0.5716966148121198],
    right_pinky3: [0, 0, 0.700459996825794, 0.7136916651095266],
  },
  [HAND_OPTIONS.POINTING]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      -0.25571491061657586, 0.25831878526299173, -0.18921051036044664,
      0.9121845605127005,
    ],
    left_thumb2: [
      0.13042022706564638, 0.6889472250967437, 0.12011880745691295,
      0.7027899810745533,
    ],
    left_thumb3: [
      0.11744310316837994, 0.6203953317293303, 0.10816669939950499,
      0.7678650369873945,
    ],
    left_index1: [
      0.04750334676440867, 0.052160745645167275, -0.026190170709024033,
      0.9971643613859014,
    ],
    left_index2: [0, 0, 0.07554714287294707, 0.9971422311805546],
    left_index3: [0, 0, 0.04065553747777228, 0.9991732218551463],
    left_middle1: [
      0.010819898825870226, 0.0234282142958473, -0.5979136262706214,
      0.8011450206324828,
    ],
    left_middle2: [0, 0, -0.8328287231796372, 0.5535307740740123],
    left_middle3: [0, 0, -0.5723126039562771, 0.8200355378596622],
    left_ring1: [
      -0.05358922107984755, 0.022035938922829526, -0.650784867136665,
      0.7570479968177433,
    ],
    left_ring2: [0, 0, -0.8054784595367819, 0.592625051126134],
    left_ring3: [0, 0, -0.6094544646624181, 0.7928210740785372],
    left_pinky1: [
      -0.10064392811975992, -0.007182700989368802, -0.6239586316109653,
      0.7749160177575979,
    ],
    left_pinky2: [0, 0, -0.8204651001794813, 0.5716966148119769],
    left_pinky3: [0, 0, -0.700459996825794, 0.7136916651095266],
    right_thumb1: [
      -0.2557116307712767, -0.25832839949274206, 0.18920181676205744,
      0.9121845605127009,
    ],
    right_thumb2: [
      0.13042237665460515, -0.6889470274834935, -0.12011760692531859,
      0.7027899810725518,
    ],
    right_thumb3: [
      0.1174450388677806, -0.6203951537773448, -0.10816561832336954,
      0.7678650369873947,
    ],
    right_index1: [
      0.047504911703514965, -0.05215932039550317, 0.026190170709035458,
      0.997164361385901,
    ],
    right_index2: [0, 0, -0.07554714287295852, 0.9971422311805537],
    right_index3: [0, 0, -0.0406555374777723, 0.9991732218551463],
    right_middle1: [
      0.010820260559965367, -0.023428047232082205, 0.5979136262706217,
      0.8011450206324827,
    ],
    right_middle2: [0, 0, -0.8328287231796371, -0.5535307740740124],
    right_middle3: [0, 0, 0.5723126039562769, 0.8200355378596624],
    right_ring1: [
      -0.053589221079847545, -0.022035938922829616, 0.650784867136665,
      0.7570479968177433,
    ],
    right_ring2: [0, 0, -0.8054784595367821, -0.5926250511261336],
    right_ring3: [0, 0, 0.6094544646624181, 0.7928210740785372],
    right_pinky1: [
      -0.10064313192763819, 0.007193848530106496, 0.6239586316111005,
      0.7749160177574891,
    ],
    right_pinky2: [0, 0, -0.8204651001793817, -0.5716966148121198],
    right_pinky3: [0, 0, 0.700459996825794, 0.7136916651095266],
  },
  [HAND_OPTIONS.RELAXED]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.10847956950414459, -0.03347645556389564, -0.0725394390130083,
      0.9908832119431391,
    ],
    left_thumb2: [
      0.046681217009551995, 0.2560136932693702, 0.031047960345993157,
      0.9650460491579856,
    ],
    left_thumb3: [
      -0.0271084012657969, -0.1432006234698494, -0.024967207199171424,
      0.9890072570933606,
    ],
    left_index1: [
      0.04513209861828771, -0.045317809002266826, -0.11654275014272394,
      0.9911241987010073,
    ],
    left_index2: [0, 0, -0.3194567978833344, 0.9476008412227832],
    left_index3: [0, 0, -0.2244622981183427, 0.9744827739490485],
    left_middle1: [
      0.021157563221117275, -0.0036328132953985853, -0.2781753038572626,
      0.9602904042580165,
    ],
    left_middle2: [0, 0, -0.2743246467108373, 0.961637139573433],
    left_middle3: [0, 0, -0.2244622981183427, 0.9744827739490485],
    left_ring1: [
      -0.029858354711528742, 0.01894311235095026, -0.27833899087805053,
      0.9598317786494508,
    ],
    left_ring2: [0, 0, -0.3601049125608708, 0.9329118135973665],
    left_ring3: [0, 0, -0.22446229811834262, 0.9744827739490485],
    left_pinky1: [
      -0.10886787925985425, 0.08348108220237543, -0.26936717698859397,
      0.9532156197529379,
    ],
    left_pinky2: [0, 0, -0.22890349950733327, 0.9734491193243211],
    left_pinky3: [0, 0, -0.15185499335714706, 0.98840278277254],
    right_thumb1: [
      0.108477863890958, 0.033477387283035204, 0.07254155964414287,
      0.990883211943139,
    ],
    right_thumb2: [
      0.04668204522592585, -0.25601359682560226, -0.031047510365949993,
      0.9650460491572508,
    ],
    right_thumb3: [
      -0.02710884806696839, 0.1432005823946927, 0.024966957663482305,
      0.9890072570933606,
    ],
    right_index1: [
      0.045130738943706936, 0.045319163064334575, 0.1165427501427353,
      0.9911241987010059,
    ],
    right_index2: [0, 0, 0.31945679788332376, 0.9476008412227869],
    right_index3: [0, 0, 0.22446229811834262, 0.9744827739490485],
    right_middle1: [
      0.021157507127375602, 0.0036331399711336653, 0.27817530385726263,
      0.9602904042580164,
    ],
    right_middle2: [0, 0, 0.2743246467108372, 0.961637139573433],
    right_middle3: [0, 0, 0.22446229811834262, 0.9744827739490485],
    right_ring1: [
      -0.029858354711528735, -0.018943112350950385, 0.2783389908780506,
      0.9598317786494507,
    ],
    right_ring2: [0, 0, 0.3601049125608708, 0.9329118135973665],
    right_ring3: [0, 0, 0.22446229811834262, 0.9744827739490485],
    right_pinky1: [
      -0.10887712517511934, -0.0834690231991449, 0.2693671769887607,
      0.9532156197528908,
    ],
    right_pinky2: [0, 0, 0.22890349950716304, 0.9734491193243611],
    right_pinky3: [0, 0, 0.15185499335714706, 0.98840278277254],
  },
  [HAND_OPTIONS.ROCK]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      -0.049366302298250266, 0.00448336392151232, 0.021200612133000663,
      0.9985456432685145,
    ],
    left_thumb2: [
      0.14173717736607916, 0.7487292211985936, 0.1305418729945373,
      0.6342506959166243,
    ],
    left_thumb3: [
      0.14130963261849866, 0.7464707082817159, 0.13014809845227954,
      0.6370828375304693,
    ],
    left_index1: [
      -0.0037855580169633392, -0.07494845676929517, -0.04188883397544665,
      0.9963000170463608,
    ],
    left_index2: [
      0.002034282209628875, 0.003182595466069185, 0.08734930916621494,
      0.9961705832688377,
    ],
    left_index3: [0, 0, 0.1045386234697098, 0.9945208274355335],
    left_middle1: [
      0.05802227945090762, -0.011853682793531543, -0.5982550015184647,
      0.7991144213751199,
    ],
    left_middle2: [0, 0, -0.8328287231796373, 0.5535307740740122],
    left_middle3: [0, 0, -0.8548535575983016, 0.5188693429578656],
    left_ring1: [
      -0.02467122727662333, 0.055592757234175644, -0.6525213241379146,
      0.755325557248707,
    ],
    left_ring2: [0, 0, -0.7760074160123683, 0.6307237828826554],
    left_ring3: [0, 0, -0.7096184341866854, 0.7045861749015778],
    left_pinky1: [
      -1.565762333544342e-5, 0.06688414115725236, -2.710505431213761e-20,
      0.9977607485847975,
    ],
    left_pinky2: [0, 0, 0.09030036587513277, 0.9959145766193088],
    left_pinky3: [0, 0, 0.13329644883405622, 0.9910762113622896],
    right_thumb1: [
      -0.04936592232636575, -0.004484031146586485, -0.021201355782411967,
      0.9985456432685144,
    ],
    right_thumb2: [
      0.14173951348118685, -0.7487290064375022, -0.13054056828919064,
      0.6342506959144487,
    ],
    right_thumb3: [
      0.14131196168647953, -0.7464704941667193, -0.13014679768222748,
      0.6370828375304693,
    ],
    right_index1: [
      -0.003787806667419739, 0.07494834315879802, 0.041888833975458076,
      0.9963000170463603,
    ],
    right_index2: [
      0.0020342683565884004, -0.0031826043207474892, -0.08734930916622634,
      0.9961705832688365,
    ],
    right_index3: [0, 0, -0.1045386234697098, 0.9945208274355335],
    right_middle1: [
      0.05802209642122862, 0.011854578665415678, 0.5982550015184648,
      0.7991144213751201,
    ],
    right_middle2: [0, 0, -0.8328287231796371, -0.5535307740740124],
    right_middle3: [0, 0, -0.854853557598302, -0.5188693429578651],
    right_ring1: [
      -0.024671227276623325, -0.05559275723417573, 0.6525213241379146,
      0.755325557248707,
    ],
    right_ring2: [0, 0, -0.7760074160123683, -0.6307237828826554],
    right_ring3: [0, 0, -0.7096184341866854, -0.7045861749015779],
    right_pinky1: [
      -2.3065885717607415e-5, -0.06688413901269386, -8.185514644028745e-18,
      0.9977607485847976,
    ],
    right_pinky2: [0, 0, -0.09030036587513439, 0.9959145766193087],
    right_pinky3: [0, 0, -0.13329644883405622, 0.9910762113622896],
  },
  [HAND_OPTIONS.SPLAY]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      -0.0005894883890978694, -0.1541475591462707, -0.05628993692195426,
      0.9864429154867548,
    ],
    left_thumb2: [
      -0.027967255750457033, -0.14773753792922512, -0.025758223890352375,
      0.9882954853657959,
    ],
    left_thumb3: [
      -0.0344226928731874, -0.18183850211672264, -0.031703769502728955,
      0.9822141866022006,
    ],
    left_index1: [
      0.012791832270114979, -0.24632404545559544, 0.05137068512228105,
      0.9677406090309688,
    ],
    left_index2: [0, 0, 0.09939547216325521, 0.9950480089490373],
    left_index3: [0, 0, 0.1353318278675527, 0.9908003312302772],
    left_middle1: [
      0.004329509154717226, -0.08153716556687096, 0.052831481768605436,
      0.9952596548217622,
    ],
    left_middle2: [0, 0, 0.09939547216325509, 0.9950480089490373],
    left_middle3: [0, 0, 0.13533182786755285, 0.9908003312302772],
    left_ring1: [
      -0.006990892998940093, 0.1316971154338429, 0.05254547202947919,
      0.9898716939940659,
    ],
    left_ring2: [0, 0, 0.09939547216325513, 0.9950480089490373],
    left_ring3: [0, 0, 0.1353318278675527, 0.9908003312302772],
    left_pinky1: [
      -0.012451811524491548, 0.2335391458364761, 0.05153843369447934,
      0.9709007207763559,
    ],
    left_pinky2: [0, 0, 0.09939547216325516, 0.9950480089490373],
    left_pinky3: [0, 0, 0.13533182786755268, 0.9908003312302772],
    right_thumb1: [
      -0.0005936778161936603, 0.15414638666819439, 0.05629310357212127,
      0.9864429154867547,
    ],
    right_thumb2: [
      -0.027967716706775107, 0.14773749555002313, 0.025757966448347533,
      0.9882954853662251,
    ],
    right_thumb3: [
      -0.034423260228648556, 0.1818384499588153, 0.031703452638215064,
      0.9822141866022006,
    ],
    right_index1: [
      0.012784441890748918, 0.24632442913356978, -0.051370685122270006,
      0.9677406090309694,
    ],
    right_index2: [0, 0, -0.09939547216326662, 0.9950480089490362],
    right_index3: [0, 0, -0.1353318278675527, 0.9908003312302772],
    right_middle1: [
      0.004328250207292323, 0.08153723240546706, -0.05283148176860544,
      0.9952596548217623,
    ],
    right_middle2: [0, 0, -0.09939547216325513, 0.9950480089490373],
    right_middle3: [0, 0, -0.1353318278675527, 0.9908003312302772],
    right_ring1: [
      -0.006990892998940096, -0.13169711543384297, -0.05254547202947919,
      0.9898716939940659,
    ],
    right_ring2: [0, 0, -0.09939547216325513, 0.9950480089490373],
    right_ring3: [0, 0, -0.1353318278675527, 0.9908003312302772],
    right_pinky1: [
      -0.012477678854921544, -0.2335377652086827, -0.0515384336943095,
      0.9709007207763649,
    ],
    right_pinky2: [0, 0, -0.09939547216342928, 0.99504800894902],
    right_pinky3: [0, 0, -0.1353318278675527, 0.9908003312302772],
  },
  [HAND_OPTIONS.THUMBS_UP]: {
    left_default: [0, 0, 0, 1],
    right_default: [0, 0, 0, 1],
    left_thumb1: [
      0.0024246048911448015, -0.1615828858928436, -0.01887102510787683,
      0.9866757201274592,
    ],
    left_thumb2: [
      -0.01629133016644087, -0.08605924835306078, -0.01500453722178261,
      0.9960438053605245,
    ],
    left_thumb3: [
      -0.039750140027147315, -0.20998083874770268, -0.03661042097329627,
      0.9762108639032818,
    ],
    left_index1: [
      0.05148480437248061, 0.008349056716580053, -0.6596731348211002,
      0.7497405973841708,
    ],
    left_index2: [0, 0, -0.8052447465403112, 0.5929425757771405],
    left_index3: [0, 0, -0.8161697960560195, 0.5778121355647314],
    left_middle1: [
      0.010819898825870226, 0.0234282142958473, -0.5979136262706214,
      0.8011450206324828,
    ],
    left_middle2: [0, 0, -0.8328287231796372, 0.5535307740740123],
    left_middle3: [0, 0, -0.5723126039562771, 0.8200355378596622],
    left_ring1: [
      -0.05358922107984755, 0.022035938922829526, -0.650784867136665,
      0.7570479968177433,
    ],
    left_ring2: [0, 0, -0.8054784595367819, 0.592625051126134],
    left_ring3: [0, 0, -0.6094544646624181, 0.7928210740785372],
    left_pinky1: [
      -0.10064392811975992, -0.007182700989368802, -0.6239586316109653,
      0.7749160177575979,
    ],
    left_pinky2: [0, 0, -0.8204651001794813, 0.5716966148119769],
    left_pinky3: [0, 0, -0.700459996825794, 0.7136916651095266],
    right_thumb1: [
      0.0024207428864179547, 0.1615825514054274, 0.018874384656204186,
      0.986675720127459,
    ],
    right_thumb2: [
      -0.016291598679933544, 0.08605922366541847, 0.015004387257899147,
      0.9960438053607746,
    ],
    right_thumb3: [
      -0.03975079518969094, 0.20998077851754762, 0.03661005506907041,
      0.9762108639032817,
    ],
    right_index1: [
      0.05148505484311741, -0.008347512032363856, 0.659673134821109,
      0.7497405973841632,
    ],
    right_index2: [0, 0, -0.8052447465403042, -0.59294257577715],
    right_index3: [0, 0, -0.8161697960560196, -0.5778121355647313],
    right_middle1: [
      0.010820260559965367, -0.023428047232082205, 0.5979136262706217,
      0.8011450206324827,
    ],
    right_middle2: [0, 0, -0.8328287231796371, -0.5535307740740124],
    right_middle3: [0, 0, 0.5723126039562769, 0.8200355378596624],
    right_ring1: [
      -0.053589221079847545, -0.022035938922829616, 0.650784867136665,
      0.7570479968177433,
    ],
    right_ring2: [0, 0, -0.8054784595367821, -0.5926250511261336],
    right_ring3: [0, 0, 0.6094544646624181, 0.7928210740785372],
    right_pinky1: [
      -0.10064313192763819, 0.007193848530106496, 0.6239586316111005,
      0.7749160177574891,
    ],
    right_pinky2: [0, 0, -0.8204651001793817, -0.5716966148121198],
    right_pinky3: [0, 0, 0.700459996825794, 0.7136916651095266],
  },
};
